import { IconButton, Tooltip, Box, Alert, LinearProgress, Grid } from "@mui/material";
import MDTypography from "components/Basics/MDTypography";
import ClearIcon from "@mui/icons-material/Clear";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useState, useEffect } from "react";
import { t } from "i18next";
import { uploadFileToS3, deleteFileFromPath } from "helpers/s3";

const InputUploadDocument = ({
	label,
	value,
	onChange,
	item = {},
	path,
	listOfApis = [],
	disabled,
	error,
	InputProps = {},
	...rest
}) => {
	const [inputId] = useState(`file-input-upload-${Math.random().toString(36).substr(2, 9)}`);
	const [errorMessage, setErrorMessage] = useState("");
	const [uploading, setUploading] = useState(false);

	useEffect(() => {
		if (errorMessage) {
			const timer = setTimeout(() => {
				setErrorMessage("");
			}, 5000);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [errorMessage]);

	const uploadFile = async file => {
		setErrorMessage("");
		setUploading(true);
		try {
			const tempAnswerItemID = `temp_${Date.now()}`;
			const uploadResult = await uploadFileToS3(file, tempAnswerItemID, "file", "fr");

			if (uploadResult?.fileObject) {
				onChange(uploadResult.fileObject);
			} else {
				setErrorMessage(t("FORMS.errorInUpload"));
			}
		} catch (err) {
			setErrorMessage(t("FORMS.errorInUpload"));
		}
		setUploading(false);
	};

	const handleFileChange = async e => {
		const file = e.target.files?.[0];
		if (file) await uploadFile(file);
	};

	const deleteImage = async () => {
		if (!value?.key) return;
		await deleteFileFromPath({ key: value.key });
		onChange(null);
	};

	return (
		<Grid
			size="grow"
			sx={{
				flexBasis: "100%",
				minWidth: "100%"
			}}
		>
			<MDTypography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
				{label}
			</MDTypography>

			{errorMessage && (
				<Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			{value?.url ? (
				<Box
					sx={{
						position: "relative",
						display: "flex",
						alignItems: "center",
						gap: 1,
						flexWrap: "nowrap",
						width: "100%",
						borderRadius: 2,
						border: "1px solid",
						borderColor: "divider",
						p: 1,
						bgcolor: "background.paper"
					}}
				>
					<InsertDriveFileIcon sx={{ fontSize: 36, color: "text.secondary" }} />
					<Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
						<MDTypography
							variant="body2"
							sx={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis" }}
						>
							{value?.name || value?.key || value?.url}
						</MDTypography>
						{value?.size && (
							<MDTypography variant="caption" color="text.secondary">
								{Math.round(value.size / 1024)} KB
							</MDTypography>
						)}
					</Box>
					<Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
						<Tooltip title={t("FORMS.LABELS.delete")}>
							<IconButton
								size="small"
								onClick={deleteImage}
								sx={{
									backgroundColor: "error.main",
									color: "white",
									"&:hover": { backgroundColor: "error.dark" },
									width: 34,
									height: 34
								}}
							>
								<ClearIcon sx={{ fontSize: 18 }} />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			) : (
				<Box
					component="label"
					htmlFor={inputId}
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 2,
						p: 2,
						borderRadius: 2,
						border: "1px dashed",
						borderColor: "divider",
						bgcolor: "background.paper",
						boxShadow: 1,
						cursor: disabled ? "not-allowed" : "pointer",
						transition: "all 0.12s ease-in-out",
						"&:hover": { transform: disabled ? "none" : "translateY(-2px)" }
					}}
				>
					<input
						id={inputId}
						type="file"
						onChange={handleFileChange}
						disabled={disabled}
						style={{ display: "none" }}
						aria-label={t("FORMS.uploadFile")}
					/>
					<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
						<CloudUploadIcon sx={{ fontSize: 40 }} />
						<Box sx={{ display: "flex", flexDirection: "column" }}>
							<MDTypography variant="body2">{t("FORMS.uploadDocument")}</MDTypography>
						</Box>
					</Box>

					{uploading && <LinearProgress sx={{ width: "100%", mt: 1 }} />}
				</Box>
			)}
		</Grid>
	);
};

export default InputUploadDocument;
